import { useState, useCallback } from 'react';
import api from '@/lib/api';
import type { ExportFormat, ProcessImageRequest } from '../types';

interface UseImageExportOptions {
  exportToDataURL: (
    format: ExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => string;
  exportToBlob: (
    format: ExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => Promise<Blob | null>;
}

export function useImageExport({ exportToDataURL, exportToBlob }: UseImageExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  /** Download image to local filesystem via browser. On iOS Safari the
   *  classic `<a download>` trick is unreliable — Safari often ignores
   *  the `download` attribute and just navigates to the blob URL,
   *  leaving the user staring at a preview tab instead of getting a
   *  save dialog. Fall back to the Web Share API there so the iOS
   *  share sheet handles it ("Save to Files", "Save to Photos", or
   *  share elsewhere). */
  const exportToLocal = useCallback(
    async (
      fileName: string,
      format: ExportFormat = 'png',
      quality = 1,
      target?: { width?: number; height?: number },
    ) => {
      setIsExporting(true);
      setExportError(null);

      try {
        const blob = await exportToBlob(format, quality, target);
        if (!blob) throw new Error('Failed to export canvas');

        const fullName = `${fileName}.${format}`;
        const isIOS =
          typeof navigator !== 'undefined' &&
          /iPad|iPhone|iPod/.test(navigator.userAgent) &&
          !('MSStream' in window);

        if (isIOS && typeof navigator !== 'undefined' && 'share' in navigator) {
          const file = new File([blob], fullName, { type: blob.type });
          const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
          if (!nav.canShare || nav.canShare({ files: [file] })) {
            try {
              await nav.share({ files: [file], title: fullName });
              return;
            } catch (shareErr) {
              // User cancelled the share sheet — that's not an error.
              if (shareErr instanceof Error && shareErr.name === 'AbortError') return;
              // Other share failures fall through to the <a download> path.
            }
          }
        }

        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fullName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        setExportError(err instanceof Error ? err.message : 'Export failed');
      } finally {
        setIsExporting(false);
      }
    },
    [exportToBlob],
  );

  /** Send canvas data to server for Sharp processing and save to Fias */
  const exportToFias = useCallback(async (request: ProcessImageRequest) => {
    setIsExporting(true);
    setExportError(null);

    try {
      const result = await api.processImage(request);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Server export failed';
      setExportError(message);
      return null;
    } finally {
      setIsExporting(false);
    }
  }, []);

  /** Quick save canvas as PNG to Fias filesystem */
  const quickSave = useCallback(
    async (fileName: string) => {
      const dataUrl = exportToDataURL('png', 1);
      if (!dataUrl) return null;

      return exportToFias({
        sourceDataUrl: dataUrl,
        operations: [],
        output: { format: 'png' },
        saveAs: { fileName },
      });
    },
    [exportToDataURL, exportToFias],
  );

  return {
    isExporting,
    exportError,
    exportToLocal,
    exportToFias,
    quickSave,
  };
}
